import { bool, number, oneOf, shape, string } from 'prop-types'

import { RoadTypes } from '../../models/common/road-types-enum'
import { OriginTypes } from '../../models/track/origin-types'
import { Track } from '../../models/track/track-enum'
import { iso8601PropType } from '../common/iso-8601-prop-type'
import { positionPropType } from '../common/position-prop-type'

const {
    ACCELERATION_RANGE,
    COUNTRY,
    COURSE,
    CUMULATED_FUEL_CONSUMPTION,
    ENERGY_CONSUMPTION,
    ENGINE_SPEED,
    ENGINE_STATE,
    FUEL_USAGE,
    GEAR,
    GPS_TIME,
    GREEN_SPEED,
    IN_CITY,
    IQR_AVERAGE_SPEED,
    IQR_FREEFLOW_SPEED,
    ODOMETER,
    ORIGIN,
    PLMN,
    POSITION,
    RECEIVE_TIME,
    ROAD_SPEED_LIMIT,
    ROAD_TYPE,
    SPEED,
    SPEED_RANGE,
    STATE
} = Track

/**
 * @typedef {object} Track
 * @property {number} [accelerationRange] - The acceleration fluctuation range in km/h*s since last position.
 * @property {string} [country] - The country
 * @property {number} [course] - The course in degrees
 * @property {number} [cumulatedFuelConsumption] - The cumulated fuel consumption in liters
 * @property {number} [engineSpeed] - Revolutions per minute (RPM) of the engine
 * @property {boolean} [engineState] - The engine state (engine on = true, engine off = false)
 * @property {number} [fuelUsage] - The fuel usage in l/100km
 * @property {number} [gear] - The gear, selected by driver
 * @property {ISO8601} gpsTime - The GPS time in ISO8601 format
 * @property {number} [greenSpeed] - The green speed in km/h
 * @property {boolean} [inCity] - The position location (in city = true / countryside = false)
 * @property {number} [iqrAverageSpeed] - The IQR average speed in km/h
 * @property {number} [iqrFreeflowSpeed] - The IQR freeflow speed in km/h
 * @property {number} [odometer] - The odometer in meters
 * @property {number} [plmn] - The PLMN code
 * @property {Position} position - The latitude and longitude values
 * @property {ISO8601} receiveTime - The receive time in ISO8601 format
 * @property {number} [roadSpeedLimit] - The road speed limit at the position of the track element in km/h
 * @property {roadType} [roadType] - The road type of the track element
 * @property {number} [speed] - The speed at the position of the track element in km/h
 * @property {boolean} [speedRange] - The speed fluctuation range in km/h since last position
 * @property {string} [state] - The state
 */
export const trackPropType = shape({
    [ACCELERATION_RANGE]: number,
    [COUNTRY]: string,
    [COURSE]: number,
    [CUMULATED_FUEL_CONSUMPTION]: number,
    [ENERGY_CONSUMPTION]: number,
    [ENGINE_SPEED]: number,
    [ENGINE_STATE]: bool,
    [FUEL_USAGE]: number,
    [GEAR]: number,
    [GPS_TIME]: iso8601PropType.isRequired,
    [GREEN_SPEED]: number,
    [IN_CITY]: bool,
    [IQR_AVERAGE_SPEED]: number,
    [IQR_FREEFLOW_SPEED]: number,
    [ODOMETER]: number,
    [ORIGIN]: oneOf(Object.values(OriginTypes)),
    [PLMN]: number,
    [POSITION]: positionPropType.isRequired,
    [RECEIVE_TIME]: iso8601PropType.isRequired,
    [ROAD_SPEED_LIMIT]: number,
    [ROAD_TYPE]: oneOf(Object.values(RoadTypes)),
    [SPEED]: number,
    [SPEED_RANGE]: number,
    [STATE]: string
})
